import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Chip,
  Stack,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  FolderOpen as FolderIcon,
  Photo as PhotoIcon,
  Collections as CollectionsIcon,
  Share as ShareIcon,
  FileDownload as DownloadIcon,
  Info as InfoIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  AddPhotoAlternate as AddPhotoIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { getDownloadURL, ref, uploadBytesResumable, getStorage } from 'firebase/storage';
import { collection, addDoc, query, where, orderBy, getDocs, Timestamp, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';

interface Photo {
  id: string;
  fileName: string;
  url: string;
  thumbnailUrl: string;
  uploadedBy: string;
  uploadDate: Date;
  size: number;
  description?: string;
  tags: string[];
  organizationId: string;
  tripId?: string;
  eventId?: string;
}

interface Album {
  id: string;
  name: string;
  description: string;
  coverPhotoUrl: string;
  photoCount: number;
  createdAt: Date;
}

interface UploadingPhoto {
  id: string;
  file: File;
  progress: number;
  status: 'uploading' | 'complete' | 'error';
  error?: string;
  previewUrl: string;
}

const OrganizationUploadPhotos: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isPersonalAccount, switchToOrganization, organization } = useOrganization();
  
  const [loading, setLoading] = useState(true);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [albums, setAlbums] = useState<Album[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [currentTab, setCurrentTab] = useState(0);
  
  const [uploadingPhotos, setUploadingPhotos] = useState<UploadingPhoto[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTags, setSearchTags] = useState<string[]>([]);
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  useEffect(() => {
    const initComponent = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        
        // Switch to organization context if needed
        if (isPersonalAccount) {
          await switchToOrganization(id);
        }
        
        await fetchPhotos();
      } catch (err) {
        console.error('Error initializing organization photos:', err);
        setError('Failed to load photos');
      } finally {
        setLoading(false);
      }
    };
    
    initComponent();
  }, [id, isPersonalAccount, switchToOrganization]);
  
  const fetchPhotos = async () => {
    if (!id) return;
    
    try {
      // Query photos for this specific organization
      const photosRef = collection(db, 'photos');
      const photosQuery = query(
        photosRef,
        where('organizationId', '==', id),
        orderBy('uploadDate', 'desc')
      );
      
      const photosSnapshot = await getDocs(photosQuery);
      const photosData: Photo[] = [];
      
      photosSnapshot.forEach((doc) => {
        const data = doc.data();
        photosData.push({
          id: doc.id,
          fileName: data.fileName,
          url: data.url,
          thumbnailUrl: data.thumbnailUrl || data.url,
          uploadedBy: data.uploadedBy,
          uploadDate: data.uploadDate instanceof Timestamp ? data.uploadDate.toDate() : new Date(data.uploadDate),
          size: data.size,
          description: data.description,
          tags: data.tags || [],
          organizationId: data.organizationId,
          tripId: data.tripId,
          eventId: data.eventId,
        });
      });
      
      // Extract all unique tags for filtering
      const allTags = Array.from(
        new Set(photosData.flatMap(photo => photo.tags || []))
      );
      setSearchTags(allTags);
      
      setPhotos(photosData);
    } catch (err) {
      console.error('Error fetching organization photos:', err);
      throw err;
    }
  };
  
  const fetchAlbums = async () => {
    try {
      // Mock albums data
      const mockAlbums: Album[] = [
        {
          id: 'album1',
          name: 'Team Building Events',
          description: 'Photos from our various team building activities',
          coverPhotoUrl: 'https://via.placeholder.com/300x200',
          photoCount: 12,
          createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 30) // 30 days ago
        },
        {
          id: 'album2',
          name: 'Office Photos',
          description: 'Our workspace and office environment',
          coverPhotoUrl: 'https://via.placeholder.com/300x200',
          photoCount: 8,
          createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 15) // 15 days ago
        },
        {
          id: 'album3',
          name: 'Product Launches',
          description: 'Documentation of our product launch events',
          coverPhotoUrl: 'https://via.placeholder.com/300x200',
          photoCount: 15,
          createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 60) // 60 days ago
        }
      ];
      
      setAlbums(mockAlbums);
    } catch (error) {
      console.error('Error fetching albums:', error);
    }
  };
  
  const handleOpenFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;
    
    const newFiles = Array.from(event.target.files);
    const newUploadingPhotos: UploadingPhoto[] = newFiles.map(file => ({
      id: `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      file,
      progress: 0,
      status: 'uploading',
      previewUrl: URL.createObjectURL(file)
    }));
    
    setUploadingPhotos(prev => [...prev, ...newUploadingPhotos]);
    setIsUploading(true);
    
    // Reset the file input
    if (event.target) {
      event.target.value = '';
    }
    
    // Start uploading the files
    newUploadingPhotos.forEach(photo => {
      uploadPhoto(photo);
    });
  };
  
  const uploadPhoto = async (uploadingPhoto: UploadingPhoto) => {
    if (!id || !organization) return;
    
    try {
      const storage = getStorage();
      const storagePath = `organizations/${id}/photos/${uploadingPhoto.file.name}`;
      const storageRef = ref(storage, storagePath);
      
      const uploadTask = uploadBytesResumable(storageRef, uploadingPhoto.file);
      
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // Update progress
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          setUploadingPhotos(prev => 
            prev.map(p => 
              p.id === uploadingPhoto.id ? { ...p, progress } : p
            )
          );
        },
        (error) => {
          // Handle error
          console.error('Upload error:', error);
          setUploadingPhotos(prev => 
            prev.map(p => 
              p.id === uploadingPhoto.id 
                ? { ...p, status: 'error', error: 'Upload failed: ' + error.message } 
                : p
            )
          );
        },
        async () => {
          // Upload completed successfully
          try {
            const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
            
            // Add the photo to Firestore
            const photoData = {
              fileName: uploadingPhoto.file.name,
              url: downloadUrl,
              thumbnailUrl: downloadUrl, // In a real app, generate a thumbnail
              uploadedBy: organization.id, // Use organization ID as creator reference
              uploadDate: new Date(),
              size: uploadingPhoto.file.size,
              description: '',
              tags: [],
              organizationId: id,
            };
            
            await addDoc(collection(db, 'photos'), photoData);
            
            // Update the uploading state
            setUploadingPhotos(prev => 
              prev.map(p => 
                p.id === uploadingPhoto.id ? { ...p, status: 'complete' } : p
              )
            );
            
            // Refresh the photos list
            await fetchPhotos();
            
            // Check if all uploads are complete
            setUploadingPhotos(prev => {
              const stillUploading = prev.some(p => p.status === 'uploading');
              if (!stillUploading) {
                setIsUploading(false);
              }
              return prev;
            });
          } catch (error) {
            console.error('Error adding photo to Firestore:', error);
            setUploadingPhotos(prev => 
              prev.map(p => 
                p.id === uploadingPhoto.id 
                  ? { ...p, status: 'error', error: 'Failed to save photo data' } 
                  : p
              )
            );
          }
        }
      );
    } catch (err) {
      console.error('Error initiating upload:', err);
      setUploadingPhotos(prev => 
        prev.map(p => 
          p.id === uploadingPhoto.id 
            ? { ...p, status: 'error', error: 'Failed to start upload' } 
            : p
        )
      );
    }
  };
  
  const handleRemoveUploadingPhoto = (id: string) => {
    setUploadingPhotos(prev => prev.filter(p => p.id !== id));
    
    // Check if any photos are still uploading
    setUploadingPhotos(prev => {
      const stillUploading = prev.some(p => p.status === 'uploading');
      if (!stillUploading) {
        setIsUploading(false);
      }
      return prev;
    });
  };
  
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };
  
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };
  
  const filteredPhotos = photos.filter(photo => {
    const matchesSearch = searchQuery === '' || 
      photo.fileName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (photo.description && photo.description.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesTags = selectedTag === '' || 
      (photo.tags && photo.tags.includes(selectedTag));
    
    return matchesSearch && matchesTags;
  });
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {organization?.name || 'Organization'} Photos
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddPhotoIcon />}
            color="primary"
            onClick={handleOpenFileDialog}
            disabled={isUploading}
          >
            Upload Photos
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Upload and manage photos for your organization
        </Typography>
        
        {isUploading && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <LinearProgress variant="determinate" value={uploadingPhotos.length > 0 ? uploadingPhotos[0].progress : 0} />
            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
              Uploading... {uploadingPhotos.length > 0 ? uploadingPhotos[0].progress : 0}%
            </Typography>
          </Box>
        )}
      </Paper>
      
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            aria-label="photo tabs"
          >
            <Tab icon={<PhotoIcon />} label="All Photos" />
            <Tab icon={<CollectionsIcon />} label="Albums" />
          </Tabs>
        </Box>
        
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {currentTab === 0 ? `${photos.length} photos` : `${albums.length} albums`}
          </Typography>
          {currentTab === 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton 
                color={viewMode === 'grid' ? 'primary' : 'default'}
                onClick={() => setViewMode('grid')}
              >
                <GridViewIcon />
              </IconButton>
              <IconButton 
                color={viewMode === 'list' ? 'primary' : 'default'}
                onClick={() => setViewMode('list')}
              >
                <ListViewIcon />
              </IconButton>
            </Box>
          )}
        </Box>
        
        <Divider />
        
        {/* Filtering and search */}
        <Paper sx={{ p: 2, mb: 4 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search photos..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ flexGrow: 1 }}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
              }}
            />
            
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {searchTags.length > 0 && (
                <>
                  <Chip 
                    label="All" 
                    color={selectedTag === '' ? 'primary' : 'default'}
                    onClick={() => setSelectedTag('')}
                  />
                  {searchTags.map(tag => (
                    <Chip
                      key={tag}
                      label={tag}
                      color={selectedTag === tag ? 'primary' : 'default'}
                      onClick={() => setSelectedTag(tag === selectedTag ? '' : tag)}
                    />
                  ))}
                </>
              )}
            </Box>
          </Box>
        </Paper>
        
        {/* Currently uploading photos */}
        {uploadingPhotos.length > 0 && (
          <Paper sx={{ p: 2, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Uploading Photos
            </Typography>
            
            <Grid container spacing={2}>
              {uploadingPhotos.map(photo => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={photo.id}>
                  <Card>
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        height="160"
                        image={photo.previewUrl}
                        alt={photo.file.name}
                        sx={{ 
                          opacity: photo.status === 'error' ? 0.5 : 1
                        }}
                      />
                      
                      {photo.status === 'complete' ? (
                        <Box 
                          sx={{ 
                            position: 'absolute', 
                            top: 8, 
                            right: 8,
                            backgroundColor: 'success.main',
                            borderRadius: '50%',
                            padding: 0.5
                          }}
                        >
                          <CheckIcon sx={{ color: 'white', fontSize: 20 }} />
                        </Box>
                      ) : photo.status === 'error' ? (
                        <Box 
                          sx={{ 
                            position: 'absolute', 
                            top: 8, 
                            right: 8,
                            backgroundColor: 'error.main',
                            borderRadius: '50%',
                            padding: 0.5
                          }}
                        >
                          <ErrorIcon sx={{ color: 'white', fontSize: 20 }} />
                        </Box>
                      ) : null}
                      
                      <IconButton
                        size="small"
                        sx={{ 
                          position: 'absolute', 
                          top: 8, 
                          left: 8,
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
                        }}
                        onClick={() => handleRemoveUploadingPhoto(photo.id)}
                      >
                        <DeleteIcon sx={{ color: 'white' }} />
                      </IconButton>
                    </Box>
                    
                    <CardContent>
                      <Typography variant="body2" noWrap sx={{ mb: 1 }}>
                        {photo.file.name}
                      </Typography>
                      
                      {photo.status === 'uploading' && (
                        <>
                          <LinearProgress variant="determinate" value={photo.progress} sx={{ mb: 1 }} />
                          <Typography variant="caption" color="text.secondary">
                            {photo.progress}% • {formatFileSize(photo.file.size)}
                          </Typography>
                        </>
                      )}
                      
                      {photo.status === 'error' && (
                        <Typography variant="caption" color="error">
                          {photo.error || 'Upload failed'}
                        </Typography>
                      )}
                      
                      {photo.status === 'complete' && (
                        <Typography variant="caption" color="success.main">
                          Upload complete • {formatFileSize(photo.file.size)}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}
        
        {/* Photos Tab */}
        {currentTab === 0 && (
          <Box sx={{ p: 2 }}>
            {filteredPhotos.length > 0 ? (
              <Grid container spacing={3}>
                {filteredPhotos.map((photo) => (
                  <Grid item key={photo.id} xs={12} sm={viewMode === 'grid' ? 6 : 12} md={viewMode === 'grid' ? 4 : 12} lg={viewMode === 'grid' ? 3 : 12}>
                    <Card 
                      sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: viewMode === 'grid' ? 'column' : 'row',
                        borderRadius: 2 
                      }}
                    >
                      <CardMedia
                        component="img"
                        image={photo.thumbnailUrl}
                        alt={photo.fileName}
                        sx={{ 
                          height: viewMode === 'grid' ? 180 : 120,
                          width: viewMode === 'grid' ? '100%' : 160,
                          objectFit: 'cover'
                        }}
                      />
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        flexGrow: 1
                      }}>
                        <CardContent sx={{ flexGrow: 1, pb: viewMode === 'grid' ? 1 : 0 }}>
                          <Typography variant="subtitle1" component="h2" noWrap>
                            {photo.fileName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(photo.uploadDate)} • {formatFileSize(photo.size)}
                          </Typography>
                          {viewMode === 'list' && photo.tags.length > 0 && (
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                              {photo.tags.map(tag => (
                                <Chip key={tag} label={tag} size="small" />
                              ))}
                            </Stack>
                          )}
                        </CardContent>
                        <CardActions>
                          <Button 
                            size="small" 
                            startIcon={<InfoIcon />}
                            onClick={() => {
                              setSelectedPhoto(photo);
                              setDetailsOpen(true);
                            }}
                          >
                            Details
                          </Button>
                          <Button 
                            size="small" 
                            startIcon={<DeleteIcon />}
                            color="error"
                            onClick={() => {
                              setSelectedPhoto(photo);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            Delete
                          </Button>
                        </CardActions>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                p: 4 
              }}>
                <PhotoIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No photos yet
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" paragraph>
                  Upload photos to start building your organization's gallery
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddPhotoIcon />}
                  onClick={handleOpenFileDialog}
                >
                  Upload Photos
                </Button>
              </Box>
            )}
          </Box>
        )}
        
        {/* Albums Tab */}
        {currentTab === 1 && (
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<FolderIcon />}
              >
                Create Album
              </Button>
            </Box>
            
            {albums.length > 0 ? (
              <Grid container spacing={3}>
                {albums.map((album) => (
                  <Grid item key={album.id} xs={12} sm={6} md={4}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2 }}>
                      <CardMedia
                        component="img"
                        height={180}
                        image={album.coverPhotoUrl}
                        alt={album.name}
                      />
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" component="h2">
                          {album.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          {album.description}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {album.photoCount} photos • Created {formatDate(album.createdAt)}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <Button size="small">View</Button>
                        <Button size="small">Edit</Button>
                        <Button size="small" color="error">Delete</Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                p: 4 
              }}>
                <CollectionsIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No albums yet
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" paragraph>
                  Create albums to organize your organization's photos
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<FolderIcon />}
                >
                  Create Album
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Paper>
      
      {/* Photo Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedPhoto && (
          <>
            <DialogTitle>
              {selectedPhoto.fileName}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={7}>
                  <img 
                    src={selectedPhoto.url} 
                    alt={selectedPhoto.fileName} 
                    style={{ 
                      width: '100%', 
                      height: 'auto',
                      borderRadius: 8,
                      maxHeight: '60vh',
                      objectFit: 'contain'
                    }} 
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <Typography variant="subtitle1" gutterBottom>
                    Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">
                        Uploaded by
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body2">
                        {selectedPhoto.uploadedBy}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">
                        Date
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body2">
                        {formatDate(selectedPhoto.uploadDate)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">
                        Size
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body2">
                        {formatFileSize(selectedPhoto.size)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">
                        Description
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body2">
                        {selectedPhoto.description}
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                    Tags
                  </Typography>
                  {selectedPhoto.tags.length > 0 ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {selectedPhoto.tags.map(tag => (
                        <Chip key={tag} label={tag} onDelete={() => {}} />
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No tags
                    </Typography>
                  )}
                  
                  <Box sx={{ display: 'flex', mt: 3 }}>
                    <TextField
                      size="small"
                      label="Add tag"
                      variant="outlined"
                      fullWidth
                    />
                    <Button sx={{ ml: 1 }}>Add</Button>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button startIcon={<DownloadIcon />}>
                Download
              </Button>
              <Button startIcon={<ShareIcon />}>
                Share
              </Button>
              <Button startIcon={<DeleteIcon />} color="error" onClick={() => {
                setDetailsOpen(false);
                setDeleteDialogOpen(true);
              }}>
                Delete
              </Button>
              <Button onClick={() => setDetailsOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>
          Delete Photo
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{selectedPhoto?.fileName}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={() => {
            // Implement delete logic here
            setDeleteDialogOpen(false);
          }} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OrganizationUploadPhotos; 